import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Review } from '../../types';
import { roundRating, formatRating } from '../../utils/rating';
import './RatingGraph.css';

interface RatingGraphProps {
  clientId?: number | null;
  reviews?: Review[];
  filters?: {
    search?: string;
    start_date?: string;
    end_date?: string;
  };
  loading?: boolean;
}

interface GraphDataPoint {
  date: string;
  averageRating: number;
  count: number;
}

const RatingGraph: React.FC<RatingGraphProps> = ({ clientId, reviews, filters, loading: externalLoading }) => {
  const [graphData, setGraphData] = useState<GraphDataPoint[]>([]);
  const [processing, setProcessing] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 300 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    processGraphData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviews, filters?.start_date, filters?.end_date]);

  // Measure container size for ResponsiveContainer
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        if (width > 0) {
          setContainerSize({ width, height: 300 });
        }
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    // Use a small delay to ensure DOM is ready
    const timeoutId = setTimeout(updateSize, 100);

    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timeoutId);
    };
  }, [graphData]);

  // Helper function to extract date string (YYYY-MM-DD) from a date string without timezone conversion
  // This ensures dates match regardless of server/client timezone differences
  const extractDateString = (dateString: string): string => {
    // If the date string already contains a date part (YYYY-MM-DD), extract it directly
    // This handles formats like "2025-12-04 10:49:44" or "2025-12-04T10:49:44"
    const dateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      return dateMatch[1];
    }
    // Fallback: parse as date and extract date part
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to create a date from YYYY-MM-DD string in local timezone
  const createDateFromString = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const processGraphData = () => {
    try {
      setProcessing(true);
      
      // Use reviews passed as prop - these are already filtered by ReviewList
      const allReviews = reviews || [];
      
      // Determine date range: use filters if provided, otherwise use review dates
      let graphStartDate: Date;
      let graphEndDate: Date;
      
      if (filters?.start_date && filters?.end_date) {
        // Use filter date range (already in YYYY-MM-DD format)
        graphStartDate = createDateFromString(filters.start_date);
        graphEndDate = createDateFromString(filters.end_date);
        graphEndDate.setHours(23, 59, 59, 999);
      } else if (filters?.start_date) {
        // Only start date provided - use it and today as end
        graphStartDate = createDateFromString(filters.start_date);
        graphEndDate = new Date();
        graphEndDate.setHours(23, 59, 59, 999);
      } else if (filters?.end_date) {
        // Only end date provided - use it and 30 days back as start
        graphEndDate = createDateFromString(filters.end_date);
        graphEndDate.setHours(23, 59, 59, 999);
        graphStartDate = new Date(graphEndDate);
        graphStartDate.setDate(graphStartDate.getDate() - 30);
        graphStartDate.setHours(0, 0, 0, 0);
      } else {
        // No filters - determine range from reviews
        if (allReviews.length === 0) {
          // No reviews and no filters - show last 30 days
          graphEndDate = new Date();
          graphEndDate.setHours(23, 59, 59, 999);
          graphStartDate = new Date(graphEndDate);
          graphStartDate.setDate(graphStartDate.getDate() - 30);
          graphStartDate.setHours(0, 0, 0, 0);
        } else {
          // Extract date strings from reviews (without timezone conversion)
          const reviewDateStrings = allReviews.map(r => extractDateString(r.created_at));
          
          // Find earliest and latest date strings
          const sortedDates = reviewDateStrings.sort();
          const earliestDateStr = sortedDates[0];
          const latestDateStr = sortedDates[sortedDates.length - 1];
          
          graphStartDate = createDateFromString(earliestDateStr);
          graphStartDate.setHours(0, 0, 0, 0);
          
          graphEndDate = createDateFromString(latestDateStr);
          graphEndDate.setHours(23, 59, 59, 999);
          
          // If range is less than 7 days, extend to show at least 7 days for better visualization
          const daysDiff = Math.ceil((graphEndDate.getTime() - graphStartDate.getTime()) / (1000 * 60 * 60 * 24));
          if (daysDiff < 7) {
            // Extend backwards to show at least 7 days
            graphStartDate = new Date(graphEndDate);
            graphStartDate.setDate(graphStartDate.getDate() - 7);
            graphStartDate.setHours(0, 0, 0, 0);
          }
        }
      }

      // Group reviews by date and calculate average rating per day
      const groupedByDate: { [key: string]: Review[] } = {};
      
      allReviews.forEach((review) => {
        // Extract date string without timezone conversion
        const date = extractDateString(review.created_at);
        if (!groupedByDate[date]) {
          groupedByDate[date] = [];
        }
        groupedByDate[date].push(review);
      });

      // Calculate average rating for each day
      const dataPoints: GraphDataPoint[] = [];
      
      // Generate all dates in the date range
      const graphDaysDiff = Math.ceil((graphEndDate.getTime() - graphStartDate.getTime()) / (1000 * 60 * 60 * 24));
      const maxDays = Math.min(graphDaysDiff, 90); // Limit to 90 days max for performance
      
      // Always show a continuous range for better visualization (including empty dates)
      for (let i = 0; i <= maxDays; i++) {
        const date = new Date(graphStartDate);
        date.setDate(date.getDate() + i);
        
        // Format date as YYYY-MM-DD without timezone conversion
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        // Skip if date is beyond end date
        if (date > graphEndDate) break;
        
        const reviewsForDate = groupedByDate[dateStr] || [];
        const averageRating = reviewsForDate.length > 0
          ? reviewsForDate.reduce((sum, review) => sum + review.rating, 0) / reviewsForDate.length
          : 0;

        dataPoints.push({
          date: dateStr,
          averageRating: roundRating(averageRating),
          count: reviewsForDate.length,
        });
      }

      setGraphData(dataPoints);
    } catch (error) {
      console.error('Failed to process graph data:', error);
      setGraphData([]);
    } finally {
      setProcessing(false);
    }
  };

  // Format date for display (MM/DD)
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="graph-tooltip">
          <p className="tooltip-date">{new Date(data.date).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
          })}</p>
          <p className="tooltip-rating">Average Rating: <strong>{formatRating(data.averageRating)}/10</strong></p>
          <p className="tooltip-count">Reviews: <strong>{data.count}</strong></p>
        </div>
      );
    }
    return null;
  };

  const isLoading = externalLoading || processing;
  
  if (isLoading) {
    return (
      <div className="rating-graph-container">
        <div className="graph-loading">Loading graph data...</div>
      </div>
    );
  }

  // Generate title based on filters
  const getGraphTitle = () => {
    if (filters?.start_date && filters?.end_date) {
      const start = new Date(filters.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const end = new Date(filters.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `Average Rating Trend (${start} - ${end})`;
    } else if (filters?.start_date) {
      const start = new Date(filters.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `Average Rating Trend (From ${start})`;
    } else if (filters?.end_date) {
      const end = new Date(filters.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `Average Rating Trend (Up to ${end})`;
    }
    // Calculate days difference for default display
    const daysDiff = graphData.length > 0 ? Math.ceil((new Date(graphData[graphData.length - 1].date).getTime() - new Date(graphData[0].date).getTime()) / (1000 * 60 * 60 * 24)) : 7;
    return `Average Rating Trend (Last ${daysDiff} Days)`;
  };

  // Show graph even if there are no reviews, as long as we have data points (date range)
  // This allows showing empty dates when filters are applied
  if (graphData.length === 0) {
    return (
      <div className="rating-graph-container">
        <div className="graph-no-data">No review data available for the selected date range</div>
      </div>
    );
  }
  
  // Check if we have any reviews with count > 0
  // But still show the graph even if all counts are 0 (to show empty dates)
  const hasData = graphData.some(d => d.count > 0);
  
  // If no data and no filters, show no data message
  // If no data but filters exist, show the graph with empty dates
  if (!hasData && !filters?.start_date && !filters?.end_date) {
    return (
      <div className="rating-graph-container">
        <div className="graph-no-data">No review data available for the selected date range</div>
      </div>
    );
  }

  return (
    <div className="rating-graph-container">
      <h3 className="graph-title">{getGraphTitle()}</h3>
      <div className="rating-graph-wrapper" ref={containerRef}>
        {containerSize.width > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={graphData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                stroke="#666"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                domain={[0, 10]}
                stroke="#666"
                style={{ fontSize: '12px' }}
                label={{ value: 'Rating', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="averageRating" 
                stroke="#667eea" 
                strokeWidth={2}
                dot={{ fill: '#667eea', r: 4 }}
                activeDot={{ r: 6 }}
                name="Average Rating"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div>Loading chart...</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RatingGraph;

